import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Define our theme color structure
export interface ThemeColors {
  background: string;
  foreground: string;
  border: string;
  chart: string[];
}

// Define our theme structure
export interface ThemeInfo {
  name: string;   // Display name
  value: string;  // Theme value (used in class names)
  colors: ThemeColors; // Colors for previews
  isDark: boolean; // Indicates if the theme is a dark theme
}

interface ThemeContextType {
  availableThemes: ThemeInfo[];
  getCurrentThemeInfo: () => ThemeInfo | undefined;
  saveThemePreference: (theme: string) => Promise<void>;
  isThemeSyncing: boolean;
}

// User object fallback to match what was expected by the original implementation
interface UserProfile {
  uiTheme?: string;
  id?: string;
}

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Define all available themes with their properties - this is the single source of truth
export const AVAILABLE_THEMES: ThemeInfo[] = [
  { value: 'default', name: 'Default', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: false },
  { value: 'cola', name: 'Cola', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: true },
  { value: 'lemon-lime', name: 'Lemon Lime', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: false },
  { value: 'berry', name: 'Berry Blast', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: true },
  { value: 'orange', name: 'Orange Fizz', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: false },
  { value: 'root-beer', name: 'Root Beer', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: true },
  { value: 'blue-raspberry', name: 'Blue Raspberry', colors: { background: '', foreground: '', border: '', chart: [] }, isDark: false },
];

// Extract just the theme values for use in next-themes
export const THEME_VALUES = AVAILABLE_THEMES.map(theme => theme.value);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { theme, setTheme } = useTheme();
  const [themeInfo, setThemeInfo] = useState<ThemeInfo[]>(AVAILABLE_THEMES);
  const [isThemeSyncing, setIsThemeSyncing] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const auth = getAuth();

  // Monitor auth state to get user data
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // User is signed in
        setUser({ 
          id: authUser.uid,
          // Add other properties as needed from your Firestore profile
          uiTheme: localStorage.getItem(`user-theme-${authUser.uid}`) || 'default'
        });
      } else {
        // User is signed out
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // When the component mounts, extract actual colors from CSS for each theme
  useEffect(() => {
    const updatedThemes = AVAILABLE_THEMES.map(themeInfo => {
      const colors = extractThemeColors(themeInfo.value);
      return { ...themeInfo, colors };
    });
    setThemeInfo(updatedThemes);
  }, []);

  // Initial theme setup - run this first
  useEffect(() => {
    // Start with default theme immediately
    setTheme('default');
  }, []); // Empty dependency array to run once on mount

  // Then load user theme when available
  useEffect(() => {
    if (user && user.uiTheme && auth.currentUser) {
      setTheme(user.uiTheme);
    }
  }, [user, setTheme, auth.currentUser]);

  // Function to save theme preference to user profile
  const saveThemePreference = async (themeValue: string) => {
    if (!auth.currentUser) return;
    
    try {
      setIsThemeSyncing(true);
      
      // First, update local state immediately
      setUser(prev => prev ? {...prev, uiTheme: themeValue} : null);
      
      // Store in localStorage as backup
      localStorage.setItem(`user-theme-${auth.currentUser.uid}`, themeValue);
      
      // Then try to update Firestore if available
      try {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, {
          uiTheme: themeValue
        });
      } catch (e) {
        console.warn('Could not save theme to Firestore, using localStorage fallback', e);
      }
      
      // Update the theme regardless
      setTheme(themeValue);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    } finally {
      setIsThemeSyncing(false);
    }
  };

  // Function to extract colors from CSS variables for a given theme
  const extractThemeColors = (themeName: string): ThemeColors => {

    const tempEl = document.createElement('div');
    tempEl.className = themeName;
    document.body.appendChild(tempEl);
    
    // Get computed style
    const style = getComputedStyle(tempEl);
    
    // Extract colors
    const colors = {
      background: style.getPropertyValue('--background').trim(),
      foreground: style.getPropertyValue('--foreground').trim(),
      border: style.getPropertyValue('--border').trim(),
      chart: [
        style.getPropertyValue('--chart-1').trim(),
        style.getPropertyValue('--chart-2').trim(),
        style.getPropertyValue('--chart-3').trim(),
        style.getPropertyValue('--chart-4').trim(),
        style.getPropertyValue('--chart-5').trim()
      ]
    };
    
    // Clean up
    document.body.removeChild(tempEl);
    
    return colors;
  };

  const getCurrentThemeInfo = (): ThemeInfo | undefined => {
    return themeInfo.find(t => t.value === theme);
  };

  return (
    <ThemeContext.Provider value={{ 
      availableThemes: themeInfo,
      getCurrentThemeInfo,
      saveThemePreference,
      isThemeSyncing
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeInfo = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeInfo must be used within a ThemeProvider');
  }
  return context;
}; 